<script>
  import { _ } from "../stores/index";
  import { ContractsRepository } from "../repositories/index";
  import {
    Flex,
    FlexItem,
    Button,
    Form,
    Submit,
    TextField,
    Modal,
    Select,
    SelectItem,
    Icon,
    Text,
    Loader,
  } from "@gl/design-system";
  import { faTrashAlt, faPlus } from "@fortawesome/pro-regular-svg-icons";
  import { createEventDispatcher, onMount } from "svelte";
  import { isValidName } from "../utils/validators";
  const dispatch = createEventDispatcher();

  export let active;
  export let selectedCon;

  let loading = {};

  let form = {};

  let customers = [];
  let authorizers = [];
  let currentAuthorizers = [];
  let authorizerToAdd = null;

  async function fetchCustomers() {
    loading.customers = true;
    try {
      const result = await ContractsRepository.getCustomers();
      customers = result.list;
      form.customerId = customers[0].id;
    } finally {
      loading.customers = false;
    }
  }

  async function fetchAuthorizers() {
    loading.authorizers = true;
    try {
      const result = await ContractsRepository.getAuthorizers();
      authorizers = result.list;
      authorizerToAdd = authorizers[0]?.id;
    } finally {
      loading.authorizers = false;
    }
  }

  function handleAddAuthorizer() {
    if (!authorizerToAdd) return;
    if (currentAuthorizers.some((a) => a === authorizerToAdd)) return;
    currentAuthorizers = [...currentAuthorizers, authorizerToAdd];
  }

  function handleRemoveAuthorizer(authorizerId) {
    currentAuthorizers = currentAuthorizers.filter((a) => a !== authorizerId);
  }

  const onSubmit = async () => {
    try {
      loading.submit = true;
      if (selectedCon) {
        await ContractsRepository.editContract(selectedCon.id, {
          name: form.name,
          userQuota: form.userQuota * 1,
          roleQuota: form.roleQuota * 1,
          groupQuota: form.groupQuota * 1,
        });

        const initialAuthIds = selectedCon.authorizers.map((a) => a.id);

        const toAdd = currentAuthorizers.filter(
          (a) => !initialAuthIds.includes(a),
        );

        const toRemove = initialAuthIds.filter(
          (a) => !currentAuthorizers.includes(a),
        );

        await Promise.all([
          ...toAdd.map((a) =>
            ContractsRepository.addAuthorizer(selectedCon.id, a),
          ),
          ...toRemove.map((a) =>
            ContractsRepository.removeAuthorizer(selectedCon.id, a),
          ),
        ]);
      } else {
        const contract = await ContractsRepository.addContract(form);
        await Promise.all(
          currentAuthorizers.map((a) =>
            ContractsRepository.addAuthorizer(contract.id, a),
          ),
        );
      }
      dispatch("success");
    } finally {
      loading.submit = false;
    }
  };

  onMount(() => {
    if (selectedCon) {
      form.name = selectedCon.name;
      form.userQuota = selectedCon.userQuota;
      form.roleQuota = selectedCon.roleQuota;
      form.groupQuota = selectedCon.groupQuota;
      currentAuthorizers = [...selectedCon.authorizers.map((a) => a.id)];
    }
    fetchCustomers();
    fetchAuthorizers();
  });
</script>

<Modal bind:active boxed on:close>
  <div class="gl-title mb-4">
    {selectedCon ? $_("contracts.edit") : $_("contracts.add")}
  </div>
  <Form validationMode="eager">
    <TextField
      bind:value={form.name}
      id="app-name"
      label={$_("common.name")}
      required={$_("validations.required")}
      maxLength={50}
      config={{ characterCount: $_("character.count") }}
    />

    <Select
      label={$_("common.customer")}
      bind:value={form.customerId}
      id="customers"
      disabled={selectedCon}
    >
      {#each customers || [] as customer}
        <SelectItem label={customer.name} value={customer.id} />
      {/each}
    </Select>

    <div class="mb-4">
      <p class="mb-2">{$_("common.authorizers")}</p>
      {#if loading.authorizers}
        <Loader status="info" />
      {:else}
      {#each currentAuthorizers as authorizerId}
        {@const authData = authorizers.find((a) => a.id === authorizerId)?.data}
        <div class="authorizer-item mb-2">
          <span>{authData?.name}</span>
          <Button
            outlined
            size="tiny"
            status="error"
            loading={loading[`remove_${authorizerId}`]}
            on:click={() => handleRemoveAuthorizer(authorizerId)}
          >
            <Icon icon={faTrashAlt} />
          </Button>
        </div>
      {/each}
      {/if}
      <Flex class="mt-3">
        <FlexItem>
          <Select bind:value={authorizerToAdd} id="authorizer-to-add">
            {#each authorizers || [] as authorizer}
              <SelectItem label={authorizer.data.name} value={authorizer.id} />
            {/each}
          </Select>
        </FlexItem>
        <FlexItem class="ml-2">
          <Button
            loading={loading.addAuthorizer}
            on:click={handleAddAuthorizer}
          >
            <Icon icon={faPlus} />
            <Text>{$_("buttons.add")}</Text>
          </Button>
        </FlexItem>
      </Flex>
    </div>

    <TextField
      bind:value={form.userQuota}
      variant="Number"
      label={$_("quota.user")}
    />
    <TextField
      bind:value={form.roleQuota}
      variant="Number"
      label={$_("quota.role")}
    />
    <TextField
      bind:value={form.groupQuota}
      variant="Number"
      label={$_("quota.group")}
    />

    <Flex>
      <FlexItem>
        <Submit
          loading={loading.submit}
          on:submit={onSubmit}
          label={selectedCon ? $_("buttons.save") : $_("buttons.add")}
        />
      </FlexItem>
      <FlexItem class="ml-4">
        <Button outlined on:click={() => dispatch("close")}>
          {$_("buttons.cancel")}
        </Button>
      </FlexItem>
    </Flex>
  </Form>
</Modal>

<style>
  .authorizer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border: 1px solid var(--gl-border, #e0e0e0);
    border-radius: 4px;
    background-color: var(--gl-background);
  }
</style>
